import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { GymProvider } from './contexts/GymContext';
import { GymBrandingProvider } from './contexts/GymBrandingContext';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import Enquiries from './components/enquiries/Enquiries';
import MembershipPlans from './components/membership/MembershipPlans';
import Members from './components/members/Members';
import Users from './components/users/Users';
import SalesReports from './components/reports/SalesReports';
import ActivityLog from './components/activity/ActivityLog';
import Payments from './components/payments/Payments';
import Profile from './components/profile/Profile';
import AccessControl from './components/access/AccessControl';
import Analytics from './components/analytics/Analytics';
import EnhancedSuperAdminDashboard from './components/superadmin/EnhancedSuperAdminDashboard';
import ForgotPassword from './components/auth/ForgotPassword';
import Layout from './components/layout/Layout';
import GlobalRetryNotification from './components/common/GlobalRetryNotification';
import Homepage from './components/homepage/Homepage';
import LandingPage from './components/landing/LandingPage';
import Blog1 from './components/blog/Blog1';
import Blog2 from './components/blog/Blog2';
import Blog3 from './components/blog/Blog3';
import ComingSoon from './components/common/ComingSoon';
import HashRedirect from './components/common/HashRedirect';
import FeaturesPage from './components/pages/FeaturesPage';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PricingPage from './components/pages/PricingPage';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode; permission?: string }> = ({ 
  children, 
  permission 
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#F9FAFB'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #E5E7EB',
          borderTop: '4px solid #4F46E5',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

const PermissionRoute: React.FC<{ children: React.ReactNode; permission: string }> = ({ 
  children, 
  permission 
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission } = usePermissions();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#F9FAFB'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #E5E7EB',
          borderTop: '4px solid #4F46E5',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  // Determine the default dashboard based on user type
  const defaultDashboard = user?.isSuperAdmin ? '/superadmin' : '/dashboard';

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={defaultDashboard} />} />
      <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to={defaultDashboard} />} />
      <Route path="/best-gym-management-software-india" element={<LandingPage />} />
      <Route path="/blog/top-10-gym-management-software-india" element={<Blog1 />} />
      <Route path="/blog/how-gym-owners-increase-membership" element={<Blog2 />} />
      <Route path="/blog/best-attendance-tracking-system-gyms" element={<Blog3 />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/support" element={<Navigate to="/coming-soon" replace />} />
      <Route path="/docs" element={<Navigate to="/coming-soon" replace />} />
      <Route path="/privacy" element={<Navigate to="/coming-soon" replace />} />
      
      {/* App Routes - Redirect /app to dashboard */}
      <Route path="/app" element={<Navigate to={defaultDashboard} replace />} />
      
      {/* Protected Routes */}
      <Route path="/superadmin" element={
        <ProtectedRoute>
          {user?.isSuperAdmin ? (
            <EnhancedSuperAdminDashboard />
          ) : (
            <Navigate to="/dashboard" replace />
          )}
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <PermissionRoute permission="view_dashboard">
          <Layout>
            <Dashboard />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/enquiries" element={
        <PermissionRoute permission="view_enquiries">
          <Layout>
            <Enquiries />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/membership-plans" element={
        <PermissionRoute permission="view_plans">
          <Layout>
            <MembershipPlans />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/members" element={
        <PermissionRoute permission="view_members">
          <Layout>
            <Members />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/users" element={
        <PermissionRoute permission="view_users">
          <Layout>
            <Users />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/reports" element={
        <PermissionRoute permission="view_reports">
          <Layout>
            <SalesReports />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/analytics" element={
        <PermissionRoute permission="view_reports">
          <Layout>
            <Analytics />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/payments" element={
        <PermissionRoute permission="view_payments">
          <Layout>
            <Payments />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/activity" element={
        <PermissionRoute permission="view_activity">
          <Layout>
            <ActivityLog />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/access-control" element={
        <PermissionRoute permission="view_access_control">
          <Layout>
            <AccessControl />
          </Layout>
        </PermissionRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <GymProvider>
          <GymBrandingProvider>
            <Router>
              <div className="App">
                <GlobalRetryNotification />
                <AppRoutes />
              </div>
            </Router>
          </GymBrandingProvider>
        </GymProvider>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;